<template>
  <v-app>
    <transition mode="out-in">
      <router-view />
    </transition>
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from "@vue/composition-api";
import { apiClient } from "./api/apiClient";
import { meService } from "./api/meService";
import { auth } from "./plugins/firebase";
import store from "./store";

export default defineComponent({
  name: "App",
  beforeMount() {
    auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Check if this is a different user than what's stored
        const storedUID = store.getters.uid;
        if (storedUID && storedUID !== user.uid) {
          console.info(
            "[Auth] Different user detected in auth state change, clearing old data",
          );
          this.clearAllUserData();
        }

        await this.exchangeFirebaseToken();
      } else {
        console.info("[Auth] User not authenticated, clearing tokens");
        this.clearAllUserData();
      }
    });
  },
  methods: {
    async exchangeFirebaseToken() {
      try {
        if (!auth.currentUser) {
          console.warn("[Auth] No current user found during token exchange");
          store.commit("setAuthStatus", "unauthenticated");
          return;
        }

        if (store.getters.isCreatingUser) {
          console.info("[Auth] User is being created, skipping token exchange");
          return;
        }

        // Check if the current Firebase user is different from the stored user
        const currentFirebaseUID = auth.currentUser.uid;
        const storedUID = store.getters.uid;

        // If UIDs don't match, clear everything and start fresh
        if (storedUID && storedUID !== currentFirebaseUID) {
          console.info("[Auth] Different user detected, clearing old data");
          this.clearAllUserData();
        }

        const accessToken = this.getLocalAccessToken();

        if (accessToken && storedUID === currentFirebaseUID) {
          console.info("[Auth] Using existing access token for same user");
          apiClient.setAccessToken(accessToken);

          const result = await this.fetchMe();

          if (!result.success) {
            console.warn(
              "[Auth] Failed to fetch user data, token may be expired",
            );
            await this.signOut("session_expired");
            return;
          }

          if (!result.data || !result.data.isAccepted) {
            console.warn("[Auth] User is not accepted");
            await this.signOut("not_accepted");
            return;
          }

          store.commit("setAuthStatus", "authenticated");
          return;
        }

        console.info("[Auth] Requesting new access token");
        const firebaseIdToken = await auth.currentUser?.getIdTokenResult();
        if (!firebaseIdToken?.token) {
          console.error("[Auth] Firebase token not found");
          store.commit("setAuthStatus", "unauthenticated");
          await this.signOut();
          return;
        }

        const accessTokenResponse = await apiClient.post<{
          accessToken: string;
          userType: "learner" | "admin" | "customer";
        }>(
          "/auth/exchange",
          { userType: "customer" },
          {
            headers: {
              Authorization: `Bearer ${firebaseIdToken.token}`,
            },
          },
        );

        if (
          !accessTokenResponse?.result?.accessToken ||
          accessTokenResponse?.result?.userType !== "customer"
        ) {
          console.error("[Auth] Invalid access token response from server");
          await this.signOut();
          return;
        }

        console.info("[Auth] Successfully obtained new access token");
        apiClient.setAccessToken(accessTokenResponse.result.accessToken);
        this.saveLocalAccessToken(accessTokenResponse.result.accessToken);

        const result = await this.fetchMe();

        if (!result.success) {
          console.error("[Auth] Failed to fetch user data with new token");
          await this.signOut("session_expired");
          return;
        }

        if (!result.data || !result.data.isAccepted) {
          console.warn("[Auth] User is not accepted");
          await this.signOut("not_accepted");
          return;
        }

        store.commit("setAuthStatus", "authenticated");
      } catch (error) {
        console.error(
          "[Auth] Token exchange failed:",
          error instanceof Error ? error.message : "Unknown error",
        );
        store.commit("setAuthStatus", "unauthenticated");
        await this.signOut();
      }
    },

    async fetchMe() {
      try {
        store.commit("setIsLoadingUser", true);
        const me = await meService.get();
        if (me) {
          store.commit("setCustomerId", me.customer.id);
          return { success: true, data: me };
        }
        // me is null - likely a 401 or other API error
        return { success: false, data: null };
      } catch (error) {
        console.error("[Auth] Failed to fetch user data:", error);
        return { success: false, data: null, error };
      } finally {
        store.commit("setIsLoadingUser", false);
      }
    },

    getLocalAccessToken() {
      return localStorage.getItem("accessToken");
    },

    saveLocalAccessToken(accessToken: string) {
      localStorage.setItem("accessToken", accessToken);
    },
    deleteLocalAccessToken() {
      localStorage.removeItem("accessToken");
    },

    clearAllUserData() {
      // Clear all possible localStorage keys that contain user data
      localStorage.removeItem("accessToken");
      localStorage.removeItem("langx-customer-console");

      // Reset store state
      store.commit("reset");

      // Clear API client
      apiClient.destroy();
    },

    async signOut(errorReason?: string) {
      await store.dispatch("signOut", errorReason);
    },
  },
});
</script>
